import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { SlideContainer, HeroBanner, BannerDecoClip, HeroHeadline, HeroBody } from './slideStyles';

const ExpandedSlideContainer = styled(SlideContainer)`
  height: 100%;
  flex: 1;
  min-height: 0;
`;


const CheckCircle = styled.div`
  width: clamp(52px, 8vw, 80px);
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background: linear-gradient(135deg, #34D399 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(52, 211, 153, 0.35);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ReadyTitle = styled(HeroHeadline)`
  margin: 0;
  color: #111827;
`;

const ReadyBody = styled(HeroBody)`
  margin-top: 0.5rem;
  max-width: 520px;
  color: #475569;
`;


const STORY_STEPS = [
  'Assessment Complete',
  'Insights Generated',
  'Recommendations Created',
  'Focus Plan Ready',
  'Start Improving',
];

const STEP_EXPLANATIONS = [
  'Responses have been captured and the assessment is now complete.',
  'Team feedback has been turned into meaningful insights.',
  'Tailored recommendations were generated for your culture priorities.',
  'A concrete focus plan has been started from the strongest findings.',
  'You\'re ready to begin improving your team\'s culture!',
];

const pulseStep = keyframes`
  0%,
  100% {
    opacity: 1;
    color: #111827;
    background: rgba(37, 99, 235, 0.12);
    box-shadow: 0 0 12px rgba(37, 99, 235, 0.08);
    text-shadow: 0 0 8px rgba(37, 99, 235, 0.08);
    transform: scale(1);
  }
  50% {
    opacity: 1;
    color: #111827;
    background: rgba(37, 99, 235, 0.14);
    box-shadow: 0 0 24px rgba(37, 99, 235, 0.16);
    text-shadow: 0 0 12px rgba(59, 130, 246, 0.14);
    transform: scale(1.02);
  }
`;

const finalSplash = keyframes`
  0%,
  100% {
    opacity: 1;
    color: #2563EB;
    background: rgba(37, 99, 235, 0.18);
    box-shadow: 0 0 18px rgba(37, 99, 235, 0.14);
    text-shadow: 0 0 10px rgba(37, 99, 235, 0.12);
    transform: scale(1);
  }
  50% {
    opacity: 1;
    color: #2563EB;
    background: rgba(37, 99, 235, 0.22);
    box-shadow: 0 0 32px rgba(37, 99, 235, 0.2);
    text-shadow: 0 0 16px rgba(37, 99, 235, 0.28);
    transform: scale(1.02);
  }
`;

const checkReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.7);
    box-shadow: 0 0 0 rgba(16, 185, 129, 0);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
    box-shadow: 0 0 18px rgba(16, 185, 129, 0.22);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 8px rgba(16, 185, 129, 0.14);
  }
`;

const stepReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StoryCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 1rem;
  padding: 1.75rem 2rem;
  width: 100%;
  max-width: none;
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media (max-width: 900px) {
    padding: 1.5rem 1.25rem;
  }

  @media (max-width: 700px) {
    padding: 1.25rem 0.9rem;
  }
`;


const StorySteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.75rem;
  margin-left: 4.5rem;
  width: 90%;
  flex: 1;
  min-height: 0;
  @media (min-height: 780px) {
    gap: 1.25rem;
  }
  @media (min-height: 860px) {
    gap: 1.0rem;
  }

  @media (max-width: 1100px) {
    margin-left: 2rem;
    width: calc(100% - 2rem);
  }

  @media (max-width: 900px) {
    margin-left: 1rem;
    width: calc(100% - 1rem);
  }

  @media (max-width: 700px) {
    margin-left: 0;
    width: 100%;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    gap: 0.5rem;
  }
`;

const StoryStepRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  flex-wrap: wrap;
  padding: 0.25rem 0;
  @media (min-height: 780px) {
    padding: 0.6rem 0;
  }
  @media (min-height: 860px) {
    padding: 0.85rem 0;
  }
  @media (max-width: 700px) {
    padding: 0.2rem 0;
  }
  &:last-child {
    border-bottom: none;
  }
`;

const StepExplanation = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  flex: 1 1 55%;
  min-width: 220px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: #334155;
  background-color: transparent !important;
  box-shadow: none !important;
  opacity: 0;
  transform: translateY(6px);
  ${props => css`
    animation: ${stepReveal} 0.5s ease-out forwards;
    animation-delay: ${props.$delay + 0.1}s;
  `}

  @media (max-width: 900px) {
    font-size: 14px;
    font-weight: 500;
  }

  @media (max-width: 700px) {
    font-size: 13px;
    margin-left: 1rem;
    font-weight: 500;
  }
`;

const StoryStep = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #111827;
  text-align: left;

  @media (max-width: 900px) {
    font-size: 14px;
  }

  @media (max-width: 700px) {
    font-size: 13px;
  }
`;

const StoryStepWithIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
`;

const StepComplete = styled.span<{ $delay: number }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #34D399 0%, #10B981 100%);
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  opacity: 0;
  transform: scale(0.8);
  box-shadow: 0 0 0 rgba(16, 185, 129, 0);
  ${props => css`
    animation: ${checkReveal} 0.45s ease-out forwards;
    animation-delay: ${props.$delay}s;
  `}
`;

const AnimatedStoryStep = styled(StoryStep)<{ $delay: number; $final?: boolean }>`
  display: grid;
  width: 100%;
  grid-template-columns: minmax(240px, 40%) minmax(220px, 1fr);
  gap: 1rem;
  align-items: center;
  animation: ${props =>
    props.$final
      ? css`
          ${finalSplash} 0.75s ease-in-out forwards;
        `
      : css`
          ${pulseStep} 0.75s ease-in-out forwards;
        `};
  animation-delay: ${props => props.$delay}s;
  padding: 0.55rem 1rem;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.06);
  box-shadow: inset 0 0 0 rgba(37, 99, 235, 0);
  transition: background 0.25s ease, box-shadow 0.25s ease;
  &:hover {
    background: rgba(37, 99, 235, 0.08);
    box-shadow: inset 0 0 0 rgba(37, 99, 235, 0.06);
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
`;


const Slide8Ready: React.FC = () => (
  <ExpandedSlideContainer>
    <HeroBanner>
      <BannerDecoClip />
      <StoryCard>
        <SectionHeader>
          <CheckCircle>
            <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
              <path
                d="M10 19L16 25L28 13"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CheckCircle>
          <div>
            <ReadyTitle>You're ready to begin!</ReadyTitle>
            <ReadyBody>
              Your team's feedback has been analyzed and your focus plan is started.
            </ReadyBody>
          </div>
        </SectionHeader>
          <StorySteps>
            {STORY_STEPS.map((step, index) => (
              <StoryStepRow key={step}>
                <AnimatedStoryStep
                  $delay={index * 1.5}
                  $final={index === STORY_STEPS.length - 1}
                >
                  <StoryStepWithIcon>
                    <StepComplete $delay={index * 1.5} aria-hidden>
                      ✓
                    </StepComplete>
                    {step}
                  </StoryStepWithIcon>
                  <StepExplanation $delay={index * 1.5}>
                    {STEP_EXPLANATIONS[index]}
                  </StepExplanation>
                </AnimatedStoryStep>
              </StoryStepRow>
            ))}
          </StorySteps>
        </StoryCard>
      </HeroBanner>
    </ExpandedSlideContainer>
  );

export default Slide8Ready;
